import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../ThemeContext';
import Button from '../../../common/button/Button';
import { CustomDialog } from '@voicecare.ai/ui-library';
import { getQuestionsByPathAndType } from '../../../../utils/apiClient';
import AddQuestion from './AddQuestion';
import './QuestionSelection.css';

const QuestionSelection = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  selectedBranch,
  selectedUsecase,
  selectedSubUsecase,
  selectedQuestions,
  setSelectedQuestions
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showStandardQuestions, setShowStandardQuestions] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasStandardQuestions, setHasStandardQuestions] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchQuestions();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter questions based on search text only
    const filtered = questions.filter(question => {
      const questionText = question.Question || question.question || '';
      const matchesSearch = questionText.toLowerCase().includes(searchText.toLowerCase());
      return matchesSearch;
    });

    // Sort questions to show custom questions first, then standard questions if enabled
    const sortedQuestions = [...filtered].sort((a, b) => {
      if (showStandardQuestions) {
        // When standard questions are enabled, sort by type (custom first)
        if (a.type === 'custom' && b.type !== 'custom') return -1;
        if (a.type !== 'custom' && b.type === 'custom') return 1;
      } else {
        // When standard questions are disabled, filter out standard questions
        if (a.type === 'standard') return 1;
        if (b.type === 'standard') return -1;
      }
      return 0;
    });

    // Filter out standard questions if checkbox is not checked
    const finalQuestions = showStandardQuestions 
      ? sortedQuestions 
      : sortedQuestions.filter(q => q.type === 'custom');

    setFilteredQuestions(finalQuestions);
  }, [questions, searchText, showStandardQuestions]);

  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const path = `${selectedBranch}-${selectedUsecase}${selectedSubUsecase ? `-${selectedSubUsecase}` : ''}`;
      console.log("Fetching questions for path:", path);
      
      // Fetch both custom and standard questions
      const [customQuestions, standardQuestions] = await Promise.all([
        getQuestionsByPathAndType({ path, type: 'custom' }),
        getQuestionsByPathAndType({ path, type: 'standard' })
      ]);

      // Transform and combine the questions
      const transformedQuestions = [
        ...(customQuestions?.questions || []).map(q => ({
          id: q._id,
          Question: q.question,
          "Expected Value": q.expected_value,
          type: q.type,
          path: q.path[0],
          metadata: q.metadata
        })),
        ...(standardQuestions?.questions || []).map(q => ({
          id: q._id,
          Question: q.question,
          "Expected Value": q.expected_value,
          type: q.type,
          path: q.path[0],
          metadata: q.metadata
        }))
      ];

      setQuestions(transformedQuestions);
      
      // Check if there are any standard questions
      const hasStandard = transformedQuestions.some(q => q.type === "standard");
      setHasStandardQuestions(hasStandard);
      
      // Reset showStandardQuestions if there are no standard questions
      if (!hasStandard) {
        setShowStandardQuestions(false);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleQuestionSelect = (question) => {
    // Only allow selection of custom questions
    if (question.type === 'standard') return;

    const isSelected = selectedQuestions.some(q => q.id === question.id);
    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const handleAddNewQuestion = (newQuestions) => {
    // Check if newQuestions is an array (multiple questions) or a single question
    const questionsToAdd = Array.isArray(newQuestions) ? newQuestions : [newQuestions];
    
    // Add each question with a unique ID
    const updatedQuestions = questionsToAdd.map((q, index) => {
      const newId = `Q${questions.length + index + 1}`;
      return { ...q, id: newId };
    });
    
    setQuestions(prev => [...prev, ...updatedQuestions]);
    setFilteredQuestions(prev => [...prev, ...updatedQuestions]);
    setShowAddQuestion(false);
    
    // Fetch questions again to get the latest data from the server
    fetchQuestions();
  };

  const toggleQuestionTypeFilter = (e) => {
    setShowStandardQuestions(e.target.checked);
  };

  // Add a function to handle closing the dialog
  const handleCloseDialog = () => {
    // If there are selected questions, show a confirmation dialog
    if (selectedQuestions.length > 0) {
      setShowCloseConfirmation(true);
    } else {
      // If no questions are selected, just close the dialog
      onClose();
    }
  };

  // Handle confirming close with selected questions
  const handleConfirmClose = () => {
    // Pass the current selection to the parent component
    onConfirm(selectedQuestions);
    setShowCloseConfirmation(false);
    onClose();
  };

  // Handle canceling close
  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  if (!isOpen) return null;

  return (
    <div className={`question-selection-container ${themeMode}`}>
      <div 
        className="question-selection-content"
        style={{
          background: currentTheme.settingPage.popupBackground,
          color: currentTheme.settingPage.textColor
        }}
      >
        <div className="question-selection-header">
          <h2>Select Questions</h2>
          <button 
            className="close-button"
            onClick={handleCloseDialog}
            style={{ color: currentTheme.settingPage.textColor }}
          >
            {themeMode === "dark" ? (
              <img src="/icons/cancel-grey.svg" width={"20vh"} />
            ) : (
              <img src="/icons/cancel-orange.svg" width={"20vh"} />
            )}
          </button>
        </div>

        <div className="question-selection-filters">
          <div className="search-container-question-selection">
            <input
              type="text"
              placeholder="Search questions..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                background: currentTheme.settingPage.themeBackground,
                color: currentTheme.settingPage.textColor,
                border: `1px solid ${currentTheme.settingPage.button}`
              }}
            />
          </div>
          <div className="filter-container">
            {hasStandardQuestions && (
              <label>
                <input
                  type="checkbox"
                  checked={showStandardQuestions}
                  onChange={toggleQuestionTypeFilter}
                  style={{
                    marginRight: '8px',
                    accentColor: currentTheme.settingPage.button
                  }}
                />
                Show Standard Questions
              </label>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="loading-container">
            <img
              className="loader"
              src={
                themeMode === "dark"
                  ? "/icons/loader_white.gif"
                  : "/icons/loader_black.gif"
              }
            />
          </div>
        ) : (
          <div className="questions-list">
            {filteredQuestions.map((question, index) => (
              <div
                key={question.id || index}
                className={`question-item ${selectedQuestions.some(q => q.id === question.id) ? 'selected' : ''} ${question.type === 'standard' ? 'standard' : ''}`}
                onClick={() => handleQuestionSelect(question)}
                style={{
                  cursor: question.type === 'standard' ? 'default' : 'pointer'
                }}
              >
                <div className="question-content">
                  <div className="question-text">{question.Question || question.question}</div>
                  <div className="question-expected">
                    Expected: {question["Expected Value"] || question.expectedValue}
                  </div>
                </div>
                <div className="question-type">
                  {question.type}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="question-selection-footer">
          <div className="footer-left">
            <Button
              label="Add New Question"
              backgroundColor={currentTheme.settingPage.button}
              color="#ffffff"
              width="150px"
              borderRadius="5px"
              height="4vh"
              onClick={() => setShowAddQuestion(true)}
            />
          </div>
          <div className="footer-right">
            <div className="selected-count">
              Selected: {selectedQuestions.length}
            </div>
            <Button
              label="Continue"
              backgroundColor={currentTheme.settingPage.button}
              color="#ffffff"
              width="120px"
              borderRadius="5px"
              height="4vh"
              onClick={() => setShowConfirmation(true)}
              disabled={selectedQuestions.length === 0}
              style={{
                opacity: selectedQuestions.length === 0 ? 0.6 : 1,
                cursor: selectedQuestions.length === 0 ? 'not-allowed' : 'pointer'
              }}
            />
            {selectedQuestions.length === 0 && (
              <div className={`continue-button-tooltip ${themeMode}-mode`}>
                Select at least one question to continue
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddQuestion && (
        <AddQuestion
          isOpen={showAddQuestion}
          onClose={() => setShowAddQuestion(false)}
          onSave={handleAddNewQuestion}
          selectedBranch={selectedBranch}
          selectedUsecase={selectedUsecase}
          selectedSubUsecase={selectedSubUsecase}
        />
      )}

      <CustomDialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Confirm Selection"
        message={`Are you sure you want to select ${selectedQuestions.length} question(s)?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={() => {
          onConfirm(selectedQuestions);
          setShowConfirmation(false);
          onClose();
        }}
        onCancel={() => setShowConfirmation(false)}
        themeMode={themeMode}
        currentTheme={currentTheme}
      />

      <CustomDialog
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        title="Confirm Close"
        message="You have selected questions. Are you sure you want to close without saving?"
        confirmLabel="Close"
        cancelLabel="Cancel"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        themeMode={themeMode}
        currentTheme={currentTheme}
      />
    </div>
  );
};

export default QuestionSelection;
